/**
 * B.11 — Game Report bottom-sheet (85dvh). Ported from screen-11 modal.
 * Modes: new / fix (correction banner) / edit / partner (body = ТЕКСТ only).
 * MirKv source → white A14 frame + «Что решили?» block. Live-calc per
 * reportModel. Submits POST /api/game-reports → OO review.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api/client";
import { useOperatorsList, type OperatorOption } from "../../hooks/useOperatorsList";
import { useTeaRooms } from "../../hooks/useTeaRooms";
import {
  type ModalGame, type CalcDefaults, type ServiceLine, type DiscountLine,
  type DiscountType, type PayState, SERVICE_OPTIONS, DISCOUNT_REASONS, CHANGE_SOURCES,
  computeCalc, buildPayload, discountAmount, lineId, rub,
} from "./reportModel";
import { Check, RDoc, RClose } from "./icons";
import { HallPayRo, type HallPayment } from "./HallPayRo";
import { CalcLine, PayField, type PadOpen } from "./reportFields";
import { TripleRow } from "./reportCells";
import { NumPadPop } from "../../components/shared/NumPadPop";
import m from "./opReport.module.css";
import { UnifansSection, WaiverPersons, type RPlayer, type Candidate, type DayOp } from "./reportPlayers"; import { useWaiverSync } from "./useWaiverSync"; import { mergeUfPlayers, savedPlayers, ufCount } from "./unifansModel"; import { achDelta, achShown, achTouchOfDraft, applyUfOps, compactOps, ufOp, ufOpsOfDraft, type AchTouch, type UfOp } from "./ufEdits";
import { WaiverBlock, useNoWaiver } from "./waiverBlock"; import { isMirKvSource, isMkMark, mkMarkRequired, MK_MARK_ERROR } from "../../utils/mirkv";
import { MediaSection } from "./MediaSection";
import { loadDraft, saveDraft, clearDraft, loadMkDraft, clearMkDraft } from "./reportDraft";
import type { CSSProperties } from "react";
import css from "./reports.module.css";

const ZERO_PAY: PayState = { cash: 0, terminal: 0, transfer: 0, prepaidUsed: 0, invoice: 0, other: 0 };
/* Ряд «чайная · МК · такси» из трёх равных ячеек: кнопка-иконка, ячейка, немая иконка-подпись. */
/* Чайная — ТОЛЬКО синий чайных из сетки DC (--blue, 14.08): рамка, триггер, чипы, плюс. */
const ddmm = (iso: string): string => { const [, m, d] = iso.split("-"); return `${d}.${m}`; };

export function GameReportModal({ game, onClose, onSuccess }: {
  game: ModalGame; onClose: () => void; onSuccess: () => void;
}) {
  const isMirkv = isMirKvSource(game.source); /* 10.10: общий детект — «mir-kvestov.ru» форма раньше не узнавала */
  /* A45: Полный|Напарник — тумблер; дефолт из check-primary, сервер держит «один полный на игру». */
  const [full, setFull] = useState(game.mode !== "partner");
  /* 11.09 (оператор через владельца): напарнику нужно видеть, кто сдаёт деньги по игре. */
  const [primaryName, setPrimaryName] = useState<string | null>(null); const [savedTick, setSavedTick] = useState(false); const partner = !full; const locked = game.mode === "edit";
  const [players, setPlayers] = useState(game.prefill?.players ?? game.bookedPlayers); const [defs, setDefs] = useState<CalcDefaults>({ questPrice: 0, teaPrice: 0, mcPrice: 0, nightSurcharge: 0, prepaid: 0 });
  const [questPrice, setQuestPrice] = useState(0); const [teaPrice, setTeaPrice] = useState(0);
  const [mcPrice, setMcPrice] = useState(0); const [nightSurcharge, setNightSurcharge] = useState(0);
  const [svcCatalog, setSvcCatalog] = useState<{ name: string; price: number }[]>([]); const [qf, setQf] = useState<{ base: number; threshold: number; extra: number } | null>(null); const [achOpen, setAchOpen] = useState(false);
  const defsRef = useRef<CalcDefaults>({ questPrice: 0, teaPrice: 0, mcPrice: 0, nightSurcharge: 0, prepaid: 0 });
  const [services, setServices] = useState<ServiceLine[]>([]); const [discounts, setDiscounts] = useState<DiscountLine[]>([]);
  const [hallPay, setHallPay] = useState<HallPayment | null>(null);   // 28.09: расчёт вписал админ зала — оплата закрыта
  const [pay, setPay] = useState<PayState>(() => game.prefill
    ? { cash: game.prefill.cash, terminal: game.prefill.terminal, transfer: game.prefill.transfer, prepaidUsed: game.prefill.prepaidUsed, invoice: game.prefill.invoice, other: 0 }
    : ZERO_PAY);
  const [change, setChange] = useState(0); const [changeSrc, setChangeSrc] = useState<{ id: string; label: string } | null>(null);
  const [emp, setEmp] = useState<OperatorOption | null>(null); const [empOpen, setEmpOpen] = useState(false); const [empSearch, setEmpSearch] = useState("");
  const [teaActive, setTeaActive] = useState(game.tea); const [teaRoomId, setTeaRoomId] = useState<string | null>(game.teaRoomId);
  const teaActiveRef = useRef(teaActive); teaActiveRef.current = teaActive; /* 06.09: для эффекта цен — не возвращать чайную, снятую оператором */
  const [teaRoomOpen, setTeaRoomOpen] = useState(false); const [cleaners, setCleaners] = useState<OperatorOption[]>([]); const [cleanerOpen, setCleanerOpen] = useState(false);
  const [taxi, setTaxi] = useState(0); const [mkStatus, setMkStatus] = useState(""); const [achItems, setAchItems] = useState<{ id: string; title: string; description: string }[]>([]);
  const [achEarned, setAchEarned] = useState<string[]>([]); const [achTouch, setAchTouch] = useState<AchTouch>({}); const achSel = useMemo(() => achShown(achEarned, achTouch), [achEarned, achTouch]); const [achHelp, setAchHelp] = useState<Set<string>>(new Set());   // 04.11 (ревью): набор с сервера + нажатое в форме (ufEdits.ts)
  const [activity, setActivity] = useState(game.prefill?.activity ?? (partner ? "" : "Вёл игру")); const [comment, setComment] = useState(game.prefill?.comment ?? "");
  const nw = useNoWaiver(game.bookingId, game.operatorId, game.mode); /* 01.10 (владелец): вейвер обязателен — фото или «вейвера нет» с причиной */
  const [ufBase, setUfBase] = useState<RPlayer[]>([]); const [ufOps, setUfOps] = useState<UfOp[]>([]); const rPlayers = useMemo(() => applyUfOps(ufBase, ufOps), [ufBase, ufOps]); const [candidates, setCandidates] = useState<Candidate[]>([]); const [dayOps, setDayOps] = useState<DayOp[]>([]); const [ufDeleg, setUfDeleg] = useState<{ to: string; toName?: string } | null>(null); const [ufJoined, setUfJoined] = useState<number | null>(null);
  /* 26.08 (владелец): со-игроки (сортировка «кто убирал»), вейвер коллеги, табличка вейвер-данных */
  const [mates, setMates] = useState<DayOp[]>([]);
  const [waiverBy, setWaiverBy] = useState<string | null>(null); const wsync = useWaiverSync(game.bookingId); const wPersons = wsync.persons; /* 03.10: табличка сохраняется сама */
  const [waiverFiles, setWaiverFiles] = useState<{ id: number; name: string; editable?: boolean }[]>([]); /* 06.09: уже загруженные фото вейвера; 18.09: editable — автор/офис могут ⟳/✕ */
  const [actInvalid, setActInvalid] = useState(false); const actRef = useRef<HTMLTextAreaElement>(null); const errRef = useRef<HTMLDivElement>(null);
  /* 27.08 (владелец): запрос ОО об отзыве о коллеге — обязательное поле отчёта */
  const [peerInvalid, setPeerInvalid] = useState(false); const peerRef = useRef<HTMLElement>(null); const [mkInvalid, setMkInvalid] = useState(false); const mkRef = useRef<HTMLElement>(null);
  const [svcOpen, setSvcOpen] = useState(false); const [discStep, setDiscStep] = useState<null | "type" | "reason">(null); const pendingType = useRef<DiscountType>("pct");
  const [submitting, setSubmitting] = useState(false); const [sent, setSent] = useState(false); const [error, setError] = useState<string | null>(null);
  const [peerSubjects, setPeerSubjects] = useState<{ id: string | null; name: string }[]>([]); const [peerData, setPeerData] = useState<Record<string, { activity: string; impression: string }>>({});

  /* peer-review requests OO assigned to this operator for this booking (R8.10; A45 — и в напарнике) */
  useEffect(() => {
    if (!game.operatorId) return;
    let alive = true; const qs = new URLSearchParams({ booking_id: String(game.bookingId), author_id: game.operatorId });
    api<{ results?: { subject_id: string | null; subject_name?: string }[] }>(`/api/peer-review?${qs}`)
      .then((r) => {
        if (!alive) return;
        const subs = (r.results ?? []).map((x) => ({ id: x.subject_id, name: x.subject_name ?? "о ком угодно" }));
        setPeerSubjects(subs);
        /* 27.08 (владелец): не пустое «поле для воды» — затравка «Имя — …», актёр продолжает мнением */
        setPeerData((p) => { const n = { ...p }; for (const s of subs) { const k = s.id ?? "any"; if (!n[k]?.activity?.trim()) n[k] = { activity: `${s.name} — `, impression: n[k]?.impression ?? "" }; } return n; });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [game.bookingId, game.operatorId]);

  /* A45: восстановление локального черновика + режим по check-primary (один раз при открытии) */
  useEffect(() => {
    if (game.mode !== "new") return;
    const d = game.prefill ? null : loadDraft(game.bookingId, game.operatorId);
    if (d) {
      setPlayers(d.players); setQuestPrice(d.questPrice); setTeaPrice(d.teaPrice); setMcPrice(d.mcPrice ?? 0); setNightSurcharge(d.nightSurcharge ?? 0); setServices(d.services); setDiscounts(d.discounts);
      setPay(d.pay); setChange(d.change); setChangeSrc(d.changeSrcId ? CHANGE_SOURCES.find((s) => s.id === d.changeSrcId) ?? null : null);
      setEmp(d.emp as OperatorOption | null); setTeaActive(d.teaActive); setTeaRoomId(d.teaRoomId); setCleaners(d.cleaners as OperatorOption[]);
      setTaxi(d.taxi); setMkStatus(d.mkStatus); setAchTouch(achTouchOfDraft(d)); setActivity(d.activity); setComment(d.comment); setFull(d.full); setUfOps(ufOpsOfDraft(d));
    }
    /* 26.08: МК-исход, проставленный кнопками в карточке игры до отчёта */
    if (!game.prefill && !d) { const mk = loadMkDraft(game.bookingId, game.operatorId); if (mk) setMkStatus(mk); }
    api<{ has_primary?: boolean; primary_operator_id?: string; primary_operator_name?: string }>(`/api/game-reports/check-primary?booking_id=${game.bookingId}`)
      .then((r) => {
        if (r.has_primary && r.primary_operator_id !== game.operatorId) {
          setFull(false);
          setPrimaryName(r.primary_operator_name || "основной оператор");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 04.09 (владелец, жалоба Ярика): при возврате отчёта на исправление форма получала
     только игроков/деньги/активность/комментарий — такси, исход МК, уборка чайной,
     сдача и «другое» стартовали с нуля, а переотправка ПЕРЕЗАПИСЫВАЛА сданное
     (такси 912 → 0). В режимах fix/edit подтягиваем свой отчёт с сервера целиком. */
  useEffect(() => {
    if (game.mode !== "fix" && game.mode !== "edit") return;
    let alive = true;
    api<{ results?: Record<string, unknown>[] }>(`/api/game-reports?booking_id=${game.bookingId}`)
      .then((r) => {
        if (!alive) return;
        const own = (r.results ?? []).find((x) => String(x.operator_id) === String(game.operatorId) && !x.archived);
        if (!own) return;
        setTaxi(Number(own.taxi_amount) || 0);
        if (own.mk_status) setMkStatus(String(own.mk_status));
        if (own.tea_cleanup) {
          setTeaActive(true); if (own.tea_room_id) setTeaRoomId(String(own.tea_room_id));
          const ops = (own._tea_cleanup_ops as { operator_id: string | number; operator_name?: string }[] | undefined) ?? [];
          if (ops.length) setCleaners(ops.map((o) => ({ id: String(o.operator_id), name: String(o.operator_name ?? o.operator_id) })));
        }
        const ch = Number(own.change_amount) || 0;
        if (ch) {
          setChange(ch); setChangeSrc(CHANGE_SOURCES.find((s) => s.id === own.change_source) ?? null);
          if (own.change_source === "operator" && own.change_source_id) setEmp({ id: String(own.change_source_id), name: String(own.change_source_name ?? own.change_source_id) });
        }
        if (Number(own.other)) setPay((p) => ({ ...p, other: Number(own.other) || 0 }));
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 12.09: после загрузки снимка — обновить список уже приложенных, чтобы он сразу
     появился в тумбах и человек видел, что фото на месте. */
  const reloadWaiverFiles = useCallback(() => {
    return api<{ waiver_files?: { id: number; name: string; editable?: boolean }[]; waiver_by?: string | null }>(`/api/game-reports/players?booking_id=${game.bookingId}`)
      .then((r) => { setWaiverFiles(r.waiver_files ?? []); setWaiverBy(r.waiver_by ?? null); })
      .catch(() => { /* список обновится при следующем открытии */ });
  }, [game.bookingId]);

  /* /players — во всех режимах (new/fix/edit): карточки, кандидаты, со-игроки, вейверы */
  useEffect(() => {
    api<{ players?: Record<string, unknown>[]; candidates?: Candidate[]; day_ops?: DayOp[]; delegation?: { to_operator_id: string; to_name?: string } | null; joined?: number | null;
      mates?: DayOp[]; waiver_by?: string | null; waiver_persons?: { fio?: string; birth_date?: string | null; phone?: string | null; unifans?: string | null }[] }>(`/api/game-reports/players?booking_id=${game.bookingId}`)
      .then((r) => {
        /* 28.08 (владелец): привязавшиеся через юнифанс появляются в отчёте
           САМИ — оператор только ставит теги; ручного добавления больше нет */
        setUfBase(mergeUfPlayers(savedPlayers(r.players ?? []), r.candidates ?? []));   // общая модель с вкладкой «Отчёты»; нажатое в форме (и в черновике) — поверх, не вместо (ufEdits.ts)
        setUfJoined(r.joined ?? null); setCandidates(r.candidates ?? []); setDayOps((r.day_ops ?? []).filter((o) => o.id !== game.operatorId));
        setUfDeleg(r.delegation ? { to: r.delegation.to_operator_id, toName: r.delegation.to_name } : null);
        setMates((r.mates ?? []).filter((o) => o.id !== game.operatorId));
        setWaiverBy(r.waiver_by ?? null); setWaiverFiles(((r as { waiver_files?: { id: number; name: string; editable?: boolean }[] }).waiver_files ?? []));
        /* 06.09: ответ /players может прийти ПОСЛЕ того, как оператор уже добавил строку вейвера —
           не затираем его ввод: серверные строки + то, что он успел добавить сам */
        const srv = (r.waiver_persons ?? []).map((p) => ({ id: (p as { id?: number }).id, fio: String(p.fio ?? ""), birth_date: String(p.birth_date ?? ""), phone: String(p.phone ?? ""), telegram: String((p as { telegram?: string | null }).telegram ?? ""), uf: p.unifans ?? null }));
        wsync.seed(srv);   // 03.10: построчно по id (useWaiverSync), свои несохранённые строки остаются первыми
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.bookingId]);

  /* 04.11: «→ передать» убрано; уже отданный блок «Юнифанс» основной возвращает себе — сразу на сервер */
  const undelegate = () => void api("/api/game-reports/unifans-delegate", { method: "POST", body: { booking_id: game.bookingId, to_operator_id: null } }).then(() => setUfDeleg(null)).catch(() => {});
  const togg = (set: React.Dispatch<React.SetStateAction<Set<string>>>) => (id: string) => set((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /* A45: комнаты/операторы сразу — лениво у брони без чайной список был пуст */
  const { operators } = useOperatorsList(true);
  const empFiltered = operators.filter((o) => o.name.toLowerCase().includes(empSearch.toLowerCase()));
  /* 26.08 (владелец): «кто убирал» — сперва со-игроки этой игры, потом филиал дня, потом остальные.
     02.09 (владелец): САМ оператор — самым первым (чаще всего убирает он). */
  const cleanerOps = useMemo(() => {
    const mateSet = new Set(mates.map((x) => x.id)); const daySet = new Set(dayOps.map((x) => x.id));
    const rank = (o: OperatorOption) => (o.id === game.operatorId ? -1 : mateSet.has(o.id) ? 0 : daySet.has(o.id) ? 1 : 2);
    return [...operators].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, "ru"));
  }, [operators, mates, dayOps, game.operatorId]);
  const { rooms: teaRooms } = useTeaRooms(game.branchId, true); const teaRoomName = teaRooms.find((r) => r.id === teaRoomId)?.name;

  // Default the room once the branch list arrives and nothing is selected yet.
  useEffect(() => { if (teaActive && !teaRoomId && teaRooms.length > 0) setTeaRoomId(teaRooms[0]!.id); }, [teaActive, teaRoomId, teaRooms]);

  /* prefill prices from booking; A45 фаза 2 — живой пересчёт при смене игроков (дебаунс).
     Оверрайды оператора не затираются: поле обновляется только если равно прошлому дефолту. */
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
    const qs = new URLSearchParams({ booking_id: String(game.bookingId), players: String(players) });
    api<CalcDefaults & { quest_price: number; tea_price: number; mc_price: number; night_surcharge: number; prepaid: number; services_catalog?: { name: string; price: number }[]; achievements?: { items: { id: string; title: string; description: string }[]; earned: string[]; hasPrimary: boolean } | null }>(`/api/game-reports/calc-price?${qs}`)
      .then((r) => {
        if (!alive) return;
        const d: CalcDefaults = {
          questPrice: Number(r.quest_price) || 0, teaPrice: Number(r.tea_price) || 0,
          mcPrice: Number(r.mc_price) || 0, nightSurcharge: Number(r.night_surcharge) || 0, prepaid: Number(r.prepaid) || 0,
        };
        const prev = defsRef.current; defsRef.current = d;
        setDefs(d);
        setQuestPrice((q) => q === prev.questPrice ? d.questPrice : q);
        /* 06.09 (жалоба Вероники, Мотель 05.09): оператор снял чайную ДО прихода цен с сервера, а ответ
           вернул ₽2 000 обратно — «слетело». Если чайной нет, дефолт не подставляем. */
        setTeaPrice((t) => t === prev.teaPrice ? (teaActiveRef.current ? d.teaPrice : 0) : t);
        setMcPrice((m) => m === prev.mcPrice ? d.mcPrice : m);
        setNightSurcharge((n) => n === prev.nightSurcharge ? d.nightSurcharge : n);
        /* 28.08: задаток — истина ДК, поле всегда из брони (сервер всё равно перепишет) */ setPay((p) => ({ ...p, prepaidUsed: d.prepaid }));
        const hp = (r as { hall_payment?: HallPayment | null }).hall_payment; setHallPay(hp ?? null); if (hp) { setPay((p) => ({ ...p, cash: hp.cash, terminal: hp.terminal, transfer: hp.transfer, invoice: hp.invoice, other: hp.other })); setChange(hp.change_amount); }
        setSvcCatalog(r.services_catalog ?? []); setQf((r as { quest_formula?: { base: number; threshold: number; extra: number } | null }).quest_formula ?? null);
        if (r.achievements) {
          setAchItems(r.achievements.items || []);
          setAchEarned(r.achievements.earned || []);
        }
      })
      .catch(() => {});
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.bookingId, players]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* A45: дропдауны закрываются тапом МИМО (раньше висели, пока не ткнёшь в чип ещё раз) */
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest) return;
      if (t.closest(`.${css.cyberPop}`) || t.closest(`.${m.chip}`) || t.closest(`.${m.teasel2}`) || t.closest(`.${m.clrow}`) || t.closest(`.${m.srcc}`)) return;
      setSvcOpen(false); setDiscStep(null); setEmpOpen(false); setTeaRoomOpen(false); setCleanerOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  const calc = useMemo(() => computeCalc({ questPrice, teaPrice, mcPrice, nightSurcharge, services, discounts, pay, change }),
    [questPrice, teaPrice, mcPrice, nightSurcharge, services, discounts, pay, change]);
  const discBase = calc.baseCalc + calc.servicesSum;

  function addService(name: string, price = 0) { setServices((s) => [...s, { id: lineId("svc"), name, amount: price }]); setSvcOpen(false); }
  function addDiscount(reason: string) { setDiscounts((d) => [...d, { id: lineId("disc"), type: pendingType.current, value: 0, reason }]); setDiscStep(null); }
  const setPayField = (k: keyof PayState, v: number) => setPay((p) => ({ ...p, [k]: v }));
  /* A27: все числа — через NumPadPop; один поповер на модалку */
  const [np, setNp] = useState<null | { anchor: HTMLElement; label: string; value: number; commit: (v: number) => void; mode?: "money" | "int"; unit?: string; max?: number }>(null); const openPad: PadOpen = (anchor, label, value, commit, opts) => setNp({ anchor, label, value, commit, ...opts });
  /* A45: «Сохранить» — черновик в localStorage, форму можно закрыть и вернуться */
  function saveLocal() {
    const ok = saveDraft(game.bookingId, game.operatorId, {
      players, questPrice, teaPrice, mcPrice, nightSurcharge, services, discounts, pay, change, changeSrcId: changeSrc?.id ?? null,
      emp: emp ? { id: emp.id, name: emp.name } : null, teaActive, teaRoomId, cleaners: cleaners.map((c) => ({ id: c.id, name: c.name })), taxi, mkStatus, achTouch, activity, comment, full, ufOps,
    });
    if (ok) { setSavedTick(true); setTimeout(() => setSavedTick(false), 1400); }
    /* 28.08 (владелец): пометка «сохранил, но не отправил» — ОО видит пунктирный чип */
    void api("/api/game-reports/draft-ping", { method: "POST", body: { booking_id: game.bookingId }, retries: 0 }).catch(() => {});
  }

  async function submit() {
    if (locked) return; // сданный заперт до возврата из OO
    /* 10.10 (владелец): отметку Мира Квестов (✕/5.0) ставит КАЖДЫЙ оператор игры — и напарник тоже */
    if (mkMarkRequired(game.source, game.date) && !isMkMark(mkStatus)) { setMkInvalid(true); setError(MK_MARK_ERROR); mkRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    if (!activity.trim()) {
      setActInvalid(true);
      setError("Заполни поле «Что делал на игре» — без него отчёт не отправить.");
      actRef.current?.focus();
      actRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    /* 27.08 (владелец): запрошенный ОО отзыв о коллеге обязателен — голая
       затравка «Имя — » или пусто не пропускаются */
    const peerMissing = peerSubjects.filter((s) => {
      const d = peerData[s.id ?? "any"];
      const body = (d?.activity ?? "").trim();
      return !((body && body !== `${s.name} —`) || (d?.impression ?? "").trim());
    });
    if (peerMissing.length > 0) {
      setPeerInvalid(true);
      setError(`ОО запросил отзыв о коллегах — заполни Teammate review: ${peerMissing.map((s) => s.name).join(", ")}.`);
      peerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    { const wErr = await nw.check(waiverFiles.length > 0, nw.isRequired(full, game.date), reloadWaiverFiles); if (wErr) { setError(wErr); nw.ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); return; } }
    /* 06.09 (владелец, жалоба Вани): основной отчёт ушёл с одним задатком при ожидаемых 4 000 —
       «деньги обнулились». Недостача больше не уходит молча: оператор подтверждает сумму явно. */
    if (full && calc.fact + 0.5 < calc.calcTotal) {
      const ok = window.confirm(`Принято ${rub(calc.fact)} при ожидаемых ${rub(calc.calcTotal)} — сумма не сходится.\nЕсли деньги были, впиши их в блоке «Оплата». Отправить как есть?`);
      if (!ok) return;
    }
    if (sent || submitting) return;            // идемпотентность — без двойной отправки
    setSubmitting(true); setError(null);
    const payload = { ...buildPayload({
      game, players, questPrice, teaPrice: teaActive ? teaPrice : 0, mcPrice, nightSurcharge,
      services, discounts, pay, change, changeSource: changeSrc?.id ?? "",
      changeSourceId: changeSrc?.id === "operator" ? (emp?.id ?? "") : "",
      teaRoomId: teaActive ? teaRoomId : null, teaCleanup: cleaners.length > 0, taxi, mkStatus, activity, comment, isPrimary: full, calc,
    }), ...nw.payload(waiverFiles.length > 0), achievements_delta: achDelta(achTouch) };   // 04.11 (ревью): ачивки — только нажатое, не снимок
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 20000);   // 20с — отчёт никогда не «зависает»
    try {
      const res = await api<{ ok?: boolean; error?: string }>("/api/game-reports", { method: "POST", body: payload, signal: ac.signal, retries: 1 });
      if (res && res.ok === false) throw new Error(res.error || "Сервер отклонил отчёт");
      // Доп-записи (уборка чайной, peer-review, игроки юнифанса) — fire-and-forget: успех отчёта от них не зависит.
      if ((ufDeleg ? ufDeleg.to === game.operatorId : !partner) && rPlayers.length > 0)   // 04.11 (ревью): не POST /players целиком — только нажатое, поверх вкладки «Отчёты»
        void api("/api/game-reports/unifans-card/sync", { method: "POST", body: { booking_id: game.bookingId, ops: compactOps(ufOps) } }).catch(() => {});
      if (cleaners.length > 0) {
        void api("/api/tea-cleanup-ops", { method: "POST", body: { booking_id: game.bookingId, operator_ids: cleaners.map((c) => c.id) } }).catch(() => {});
      }
      for (const s of peerSubjects) {
        const d = peerData[s.id ?? "any"];
        /* нетронутая затравка «Имя — » отзывом не считается */
        const body = (d?.activity ?? "").trim();
        if (d && ((body && body !== `${s.name} —`) || d.impression.trim())) {
          void api("/api/peer-review/report", { method: "POST", body: { booking_id: game.bookingId, subject_id: s.id, activity: d.activity, impression: d.impression } }).catch(() => {});
        }
      }
      /* 12.09: вейверы здесь больше не грузим — они уходят сразу при выборе (WaiverAttach).
         Отправка вместе с отчётом загоняла в тупик: отчёт принят, фото не долетело, а
         приложить заново уже нельзя — сдача закрыта до возврата из ОО. */
      // табличка вейвер-данных — replace-set по брони (26.08, владелец); 03.10: сохраняется сама, здесь — дописать итог (useWaiverSync)
      void wsync.flush();
      // медиа здесь больше не грузим: MediaSection отправляет их сразу при выборе (23.08)
      setSubmitting(false);
      clearDraft(game.bookingId, game.operatorId); clearMkDraft(game.bookingId, game.operatorId); nw.clear();
      setSent(true);                           // явный success: оверлей «Отчёт сдан»
      setTimeout(onSuccess, 1400);
    } catch (e: unknown) {
      setSubmitting(false); nw.onServerError(e, reloadWaiverFiles);
      setError(ac.signal.aborted
        ? "Сервер не ответил за 20 секунд — отчёт НЕ отправлен. Проверь связь и попробуй ещё раз."
        : (e instanceof Error ? e.message : "Не удалось отправить отчёт. Попробуй ещё раз."));
      setTimeout(() => errRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    } finally {
      clearTimeout(timer);
    }
  }

  /* 06.09 (владелец, видео): в iOS-WebView прокручиваемая область кабинета (.main, -webkit-overflow-scrolling)
     становится отдельным слоем, и fixed-модалка внутри неё оказывалась ПОД липкой шапкой и вкладками.
     Портал в body выводит её из этого слоя — как docked-TextPad и PhonePad. */
  return createPortal(
    <>
      <div className={`${css.backdrop} ${css.open}`} onClick={onClose} />
      <div className={`${css.sheet} ${css.open} ${isMirkv ? css.mirkv : ""}`} role="dialog" aria-modal="true">
        {sent ? (
          <div className={css.sentOverlay} aria-live="assertive"><div className={css.sentBadge}><Check /><span>Отчёт сдан</span></div><div className={css.sentSub}>Отправлен в OO на проверку</div></div>
        ) : null}
        <div className={css.sheetBody}>
          <div className={css.handle} />
          <div className={css.sheetHead}>
            {/* A45: одна строка — заголовок + игра; без ника оператора, клиента и тумблера
                (режим определяет расстановка/check-primary, не ручной переключатель) */}
            <div className={css.shTop}>
              <span className={css.docIco}><RDoc /></span>
              <span className={css.shTitle} style={{ flex: "0 0 auto" }}>
                {game.mode === "fix" ? "Исправление" : locked ? "Отчёт · отправлен" : "Отчёт об игре"}
              </span>
              <span className={css.meta} style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <b>{game.shortName}</b> · <b>{ddmm(game.date)}</b> · <b>{game.time}</b>{partner ? " · напарник" : ""}
              </span>
              <button type="button" className={css.shClose} onClick={onClose} aria-label="Закрыть"><RClose /></button>
            </div>
            {game.mode === "fix" && game.correctionReason ? <div className={`${css.returnBanner} ${css.show}`}><b>↩ OO:</b> {game.correctionReason}</div> : null}
          </div>

          <div className={css.sheetMain} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* 11.09 (оператор): без объяснения пустой отчёт читался как «заполнил неправильно» */}
            {partner ? (
              <div className={css.partnerNote}>
                Вы напарник на этой игре: всю выручку сдаёт {primaryName ? <b>{primaryName}</b> : "основной оператор"} —
                основным становится тот, кто первым отправил отчёт. Если деньги принимали вы, передайте их ему,
                чтобы он внёс всю сумму. От вас — активность, комментарий и медиа.
              </div>
            ) : null}
            {!partner ? (
                <div className={m.calcpay}>
                  <section className={`${m.hud} ${m.cyan}`}>
                    <div className={m.hh}>Расчёт<span className={m.plr}>игроков: {game.bookedPlayers} ▸ <button type="button" className={m.fct} title="тап: нумпад"
                      onClick={(e) => openPad(e.currentTarget, "Игроки", players, (v) => setPlayers(Math.max(1, v)), { mode: "int", unit: "чел", max: 100 })}>{players}</button></span></div>
                    {qf && qf.base > 0 ? <div className={m.brief}>база {rub(qf.base)}{qf.extra > 0 ? ` до ${qf.threshold} + ${rub(qf.extra)}/чел` : ""}</div> : null}
                    <CalcLine label="Квест" value={questPrice} def={defs.questPrice} onChange={setQuestPrice} pad={openPad} />
                    {teaActive || teaPrice > 0 ? <CalcLine label="Чайная" value={teaPrice} def={defs.teaPrice} onChange={setTeaPrice} ooZero pad={openPad} /> : null}
                    {mcPrice > 0 || defs.mcPrice > 0 ? <CalcLine label="МК" value={mcPrice} def={defs.mcPrice} onChange={setMcPrice} ooZero pad={openPad} /> : null}
                    {nightSurcharge > 0 || defs.nightSurcharge > 0 ? <CalcLine label="Ночной" value={nightSurcharge} def={defs.nightSurcharge} onChange={setNightSurcharge} ooZero pad={openPad} /> : null}
                    {services.map((s) => (
                      <div key={s.id} className={m.svc}>
                        <span className={m.nm}>{s.name}</span>
                        <button type="button" className={m.sum}
                          onClick={(e) => openPad(e.currentTarget, s.name, s.amount, (v) => setServices((arr) => arr.map((x) => x.id === s.id ? { ...x, amount: v } : x)))}>{rub(s.amount)}</button>
                        <button type="button" className={m.rm} onClick={() => setServices((arr) => arr.filter((x) => x.id !== s.id))}>✕</button>
                      </div>
                    ))}
                    {discounts.map((d) => (
                      <div key={d.id} className={m.svc}>
                        <span className={m.tchip}>{d.type === "pct" ? "%" : "₽"}</span>
                        <button type="button" className={m.sum}
                          onClick={(e) => openPad(e.currentTarget, "Скидка", d.value, (v) => setDiscounts((arr) => arr.map((x) => x.id === d.id ? { ...x, value: v } : x)), d.type === "pct" ? { mode: "int", unit: "%", max: 100 } : undefined)}>{d.type === "pct" ? `${d.value}%` : rub(d.value)}</button>
                        {(DISCOUNT_REASONS as readonly string[]).includes(d.reason) ? <span className={m.nm} title={d.reason}>{d.reason}</span>
                          : <input className={m.whyin} placeholder="за что?" value={d.reason}
                              onChange={(e) => setDiscounts((arr) => arr.map((x) => x.id === d.id ? { ...x, reason: e.target.value } : x))} />}
                        <span className={m.res}>−{rub(discountAmount(d, discBase)).slice(1)}</span>
                        <button type="button" className={m.rm} onClick={() => setDiscounts((arr) => arr.filter((x) => x.id !== d.id))}>✕</button>
                      </div>
                    ))}
                    <div className={m.chips}>
                      <span className={css.addWrap}>
                        <button type="button" className={m.chip} onClick={() => { setSvcOpen((o) => !o); setDiscStep(null); }}>+ услуга</button>
                        {svcOpen ? (
                          <div className={`${css.cyberPop} ${css.popDown} ${css.open}`}>
                            {(svcCatalog.length ? svcCatalog : SERVICE_OPTIONS.map((n) => ({ name: n, price: 0 }))).map((s) => (
                              <button key={s.name} type="button" className={css.popItem} onClick={() => addService(s.name, s.price)}>{s.name}{s.price > 0 ? ` · ${rub(s.price)}` : ""}</button>
                            ))}
                            <button type="button" className={`${css.popItem} ${css.popCustom}`} onClick={() => addService("Своя услуга")}>+ Своя услуга</button>
                          </div>
                        ) : null}
                      </span>
                      <span className={css.addWrap}>
                        <button type="button" className={m.chip} onClick={() => { setDiscStep((s) => (s ? null : "type")); setSvcOpen(false); }}>+ скидка</button>
                        {discStep === "type" ? (
                          <div className={`${css.cyberPop} ${css.popDown} ${css.open} ${m.fitPop}`}>
                            <button type="button" className={css.popItem} onClick={() => { pendingType.current = "pct"; setDiscStep("reason"); }}>Процент %</button>
                            <button type="button" className={css.popItem} onClick={() => { pendingType.current = "fix"; setDiscStep("reason"); }}>Сумма ₽</button>
                          </div>
                        ) : discStep === "reason" ? (
                          <div className={`${css.cyberPop} ${css.popDown} ${css.open} ${m.fitPop}`}>
                            {DISCOUNT_REASONS.map((r) => (
                              <button key={r} type="button" className={css.popItem} onClick={() => addDiscount(r)}>{r}</button>
                            ))}
                            <button type="button" className={`${css.popItem} ${css.popCustom}`} onClick={() => addDiscount("")}>+ Своя причина</button>
                          </div>
                        ) : null}
                      </span>
                    </div>
                    <div className={m.total}><span>Итого</span><span>{rub(calc.calcTotal)}</span></div>
                    <div className={`${m.fact} ${calc.diff === 0 ? m.dok : m.dbad}`}><span>Факт</span><span className={m.v}>{rub(calc.fact)}</span></div>
                  </section>
                  <section className={`${m.hud} ${m.orange}`}>
                    <div className={m.hh}>Оплата</div>
                    {hallPay ? <HallPayRo p={hallPay} prepaid={pay.prepaidUsed} m={m} /> : (<>
                    <PayField label="Нал" value={pay.cash} onChange={(v) => setPayField("cash", v)} pad={openPad} />
                    <PayField label="Терм" value={pay.terminal} onChange={(v) => setPayField("terminal", v)} pad={openPad} />
                    <PayField label="Перев" value={pay.transfer} onChange={(v) => setPayField("transfer", v)} pad={openPad} />
                    {/* 28.08 (владелец): задаток — истина ДК, оператор не правит */}
                    <div className={m.payRo} title="Задаток берётся из брони (ДК)"><span>ЗДТК</span><b>₽{pay.prepaidUsed}</b><i>из брони</i></div>
                    <PayField label="Р/С" value={pay.invoice} onChange={(v) => setPayField("invoice", v)} pad={openPad} />
                    <PayField label="Друг." value={pay.other} onChange={(v) => setPayField("other", v)} pad={openPad} />
                    <PayField label="Сдача" value={change} onChange={setChange} pad={openPad} last />
                    {change > 0 ? (<>
                      <div className={m.srcl}>откуда сдача</div>
                      <div className={m.srcgrid}>
                        {CHANGE_SOURCES.map((s) => (
                          <button key={s.id} type="button" className={`${m.srcc} ${changeSrc?.id === s.id ? m.on : ""}`}
                            onClick={() => { setChangeSrc(s); if (s.id === "operator") { setEmpOpen(true); } else { setEmp(null); setEmpOpen(false); } }}>
                            {s.id === "operator" && emp ? emp.name : s.label}
                          </button>
                        ))}
                      </div>
                      {empOpen ? (
                        <div className={`${css.cyberPop} ${css.popDown} ${css.open}`}>
                          <div className={css.empSearchWrap}>
                            <input className={css.empSearchInput} placeholder="Поиск по имени…" value={empSearch} autoFocus
                              onChange={(e) => setEmpSearch(e.target.value)} />
                          </div>
                          {empFiltered.length === 0 ? <div className={css.empEmpty}>Ничего не найдено</div> : null}
                          {empFiltered.map((o) => (
                            <button key={o.id} type="button" className={css.popItem}
                              onClick={() => { setEmp(o); setEmpOpen(false); }}>{o.name}</button>
                          ))}
                        </div>
                      ) : null}
                    </>) : null}
                    </>)}
                  </section>
                </div>
            ) : null}
            {/* ЧАЙНАЯ · МК · ТАКСИ — порт ряда макета; напарнику остаётся только такси */}
            <TripleRow partner={partner} isMirkv={isMirkv}
              teaActive={teaActive} teaRoomName={teaRoomName ?? null} setTeaActive={(v) => { setTeaActive(v); if (!v) setTeaPrice(0); }}
              teaRoomOpen={teaRoomOpen} setTeaRoomOpen={setTeaRoomOpen} teaRooms={teaRooms}
              setTeaRoomId={(id) => { setTeaRoomId(id); const r = teaRooms.find((x) => x.id === id); if (r && r.price > 0) setTeaPrice(r.price); /* тариф из настроек */ }}
              cleaners={cleaners} setCleaners={setCleaners} cleanerOpen={cleanerOpen} setCleanerOpen={setCleanerOpen}
              operators={cleanerOps} taxi={taxi} setTaxi={setTaxi} mkStatus={mkStatus} setMkStatus={(v) => { setMkStatus(v); if (v) { setMkInvalid(false); setError((e) => (e === MK_MARK_ERROR ? null : e)); } }} mkInvalid={mkInvalid} mkRef={mkRef} pad={openPad} />

            {/* ЮНИФАНС (A45 фаза 3): у основного — свой/отдан, у делегата — его. 04.11: делегат видит блок и до сдачи основного
                (раньше он считался «полным» и видел «заполняет <сам> · вернуть» — блок не заполнял никто). */}
            {(() => {
              const v = ufDeleg ? (ufDeleg.to === game.operatorId ? "delegate" as const : !partner ? "given" as const : null) : !partner ? "own" as const : null;
              return v ? (
                <UnifansSection variant={v} toName={ufDeleg?.toName} onReturn={undelegate} count={ufJoined == null ? null : ufCount(ufJoined, players)}
                  ach={{ items: achItems, sel: achSel, help: achHelp, open: achOpen, onOpen: () => setAchOpen((o) => !o), onSel: (id) => setAchTouch((t) => ({ ...t, [id]: !achShown(achEarned, t).has(id) })), onHelp: togg(setAchHelp) }}
                  players={rPlayers} onOp={(i, x) => setUfOps((o) => [...o, ufOp(applyUfOps(ufBase, o)[i]!, x)])} />
              ) : null;
            })()}

            {/* Медиа/«тайминги» (23.08, Илья): независимы от отчёта — грузятся сразу и сразу видны в Юнифансе; доступно и напарнику */}
            <MediaSection bookingId={game.bookingId} />

            {peerSubjects.length > 0 ? (
              <section ref={peerRef} className={`${m.hud} ${m.cyan}`}>
                <div className={m.hh}>Teammate review<span className={m.req}>*</span></div>
                {peerSubjects.map((s, i) => {
                  const key = s.id ?? "any";
                  const d = peerData[key] ?? { activity: "", impression: "" };
                  const body = d.activity.trim();
                  const filled = (body !== "" && body !== `${s.name} —`) || d.impression.trim() !== "";
                  return (<div key={key}>
                    <div className={m.who} style={i > 0 ? { marginTop: 10 } : undefined}>{s.name}</div>
                    {/* A45: один текст на коллегу; 27.08 — затравка «Имя — », подсказка про мнение; поле обязательное */}
                    <textarea className={`${m.ta} ${peerInvalid && !filled ? css.invalid : ""}`} value={d.activity} placeholder={`${s.name} — выскажи мнение: что делал(а), как справился(ась)…`}
                      onChange={(e) => { setPeerData((p) => ({ ...p, [key]: { ...d, activity: e.target.value } })); const t = e.target.value.trim(); if (t && t !== `${s.name} —`) { setPeerInvalid(false); setError(null); } }} />
                  </div>);
                })}
              </section>
            ) : null}

            <section className={`${m.hud} ${m.purple}`}>
              <div className={m.hh}>Game review</div>
              <div className={m.lbl}>Что делал на игре<span className={m.req}>*</span></div>
              <textarea ref={actRef} className={`${m.ta} ${actInvalid ? css.invalid : ""}`} value={activity}
                placeholder="Основной оператор, актёр, помощник…"
                onChange={(e) => { setActivity(e.target.value); if (e.target.value.trim()) { setActInvalid(false); setError(null); } }} />
              <div className={m.lbl}>Комментарий о команде</div>
              <textarea className={m.ta} value={comment} placeholder="Кратко о команде, чтобы коллеги знали…"
                onChange={(e) => setComment(e.target.value)} style={{ minHeight: 50 }} />
              <WaiverBlock nw={nw} bookingId={game.bookingId} required={nw.isRequired(full, game.date)}
                attachedBy={waiverBy} existing={waiverFiles} onUploaded={reloadWaiverFiles} />
              {/* табличка данных с вейверов: ФИО · др · телефон (26.08, владелец) */}
              <WaiverPersons bookingId={game.bookingId} persons={wPersons} onPersons={wsync.edit} candidates={candidates} saveState={wsync.state} />
            </section>

            {error ? <div className={css.modalErr}>{error}</div> : null}

            {/* футер в ПОТОКЕ — в самом низу контента, не прилип к экрану (мок v41) */}
            <div className={m.foot}>
              {locked ? (
                <button type="button" className={`${m.btn} ${m.esc}`} style={{ gridColumn: "1 / -1" }} onClick={onClose}>ESC</button>
              ) : (<>
                <button type="button" className={`${m.btn} ${m.save} ${savedTick ? m.ok2b : ""}`} onClick={saveLocal}>{savedTick ? "✓ Сохранено" : "Сохранить"}</button>
                <button type="button" className={`${m.btn} ${m.send}`} onClick={submit} disabled={submitting}>{sent ? "✓ Отправлено" : submitting ? "Отправка…" : "⇪ Отправить"}</button>
                <button type="button" className={`${m.btn} ${m.esc}`} onClick={onClose}>ESC</button>
              </>)}
              <div className={m.hint}>{locked ? "Отчёт отправлен в OO · правки — только если OO вернёт на доработку" : "Сохранённый можно править · отправленный — только после возврата из OO"}</div>
            </div>
          </div>
        </div>
      </div>
      <NumPadPop open={!!np} anchor={np?.anchor ?? null} label={np?.label} mode={np?.mode ?? "money"} unit={np?.unit} max={np?.max}
        value={np?.value || ""} onCommit={(v) => np?.commit(v)} onClose={() => setNp(null)} />
    </>,
    document.body,
  );
}
